import { useState } from "react";
import WfEmployeeSelect from "./WfEmployeeSelect";
import WfShiftSelect from "./WfShiftSelect";
import DatePickerField from "../common/DatePickerField";
import { formatLocalDate } from "../../Moduels/attendance/rosterValidation";
import { rosterTheme as t } from "./rosterTheme";

export default function BulkAssignPanel({
  roster,
  shifts,
  canEdit,
  onBulkAssign,
  onBulkCopy,
  onImportCsv,
  onAssignError,
}) {
  const [shiftId, setShiftId] = useState("");
  const [fromDate, setFromDate] = useState(roster?.period_start || "");
  const [toDate, setToDate] = useState(roster?.period_end || "");
  const [employeeIds, setEmployeeIds] = useState([]);
  const [copySourceFrom, setCopySourceFrom] = useState("");
  const [copySourceTo, setCopySourceTo] = useState("");
  const [copyTargetFrom, setCopyTargetFrom] = useState("");
  const [busy, setBusy] = useState(false);

  if (!canEdit) return null;

  const addEmployee = (_, emp) => {
    if (!emp?.employee_id) return;
    setEmployeeIds((prev) =>
      prev.includes(emp.employee_id) ? prev : [...prev, emp.employee_id]
    );
  };

  const runBulkMonth = async () => {
    if (!shiftId) {
      onAssignError?.({
        code: "SHIFT_REQUIRED",
        title: "Shift required",
        message: "Select a shift before applying bulk assign.",
        field: "shift_id",
      });
      return;
    }
    if (!fromDate || !toDate) {
      onAssignError?.({
        code: "DATE_REQUIRED",
        title: "Date required",
        message: "Choose a from and to date within the roster period.",
        field: "work_date",
      });
      return;
    }
    if (!employeeIds.length) {
      onAssignError?.({
        code: "EMPLOYEE_REQUIRED",
        title: "Employee required",
        message: "Add at least one employee to the bulk list.",
        field: "employee_id",
      });
      return;
    }
    setBusy(true);
    try {
      const start = new Date(fromDate + "T00:00:00");
      const end = new Date(toDate + "T00:00:00");
      const assignments = [];
      for (const empId of employeeIds) {
        const cur = new Date(start);
        while (cur <= end) {
          assignments.push({
            employee_id: empId,
            work_date: formatLocalDate(cur),
            shift_id: shiftId,
          });
          cur.setDate(cur.getDate() + 1);
        }
      }
      await onBulkAssign(assignments);
      setEmployeeIds([]);
    } finally {
      setBusy(false);
    }
  };

  const runCopy = async () => {
    if (!copySourceFrom || !copySourceTo || !copyTargetFrom) {
      onAssignError?.({
        code: "DATE_REQUIRED",
        title: "Dates required",
        message: "Fill source range and target start date for copy.",
        field: "work_date",
      });
      return;
    }
    setBusy(true);
    try {
      await onBulkCopy({
        source_from: copySourceFrom,
        source_to: copySourceTo,
        target_from: copyTargetFrom,
        employee_ids: employeeIds.length ? employeeIds : null,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`${t.cardPad} space-y-4`}>
      <h3 className={t.cardHeader}>Bulk tools</h3>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="space-y-3 border rounded-lg p-3">
          <p className="text-sm font-medium text-gray-700">Assign shift to employees (date range)</p>
          <WfShiftSelect shifts={shifts} value={shiftId} onChange={setShiftId} />
          <div className="grid grid-cols-2 gap-2">
            <DatePickerField compact value={fromDate} onChange={setFromDate} min={roster?.period_start} max={roster?.period_end || toDate} placeholder="From" />
            <DatePickerField compact value={toDate} onChange={setToDate} min={fromDate || roster?.period_start} max={roster?.period_end} placeholder="To" />
          </div>
          <WfEmployeeSelect onChange={addEmployee} placeholder="Add employee to bulk list…" />
          {employeeIds.length > 0 && (
            <p className="text-xs text-gray-500">{employeeIds.length} employee(s) selected</p>
          )}
          <button
            type="button"
            disabled={busy}
            onClick={runBulkMonth}
            className={`w-full ${t.btnPrimary} disabled:opacity-50`}
          >
            Apply to range
          </button>
        </div>

        <div className="space-y-3 border rounded-lg p-3">
          <p className="text-sm font-medium text-gray-700">Copy previous period</p>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <DatePickerField compact value={copySourceFrom} onChange={setCopySourceFrom} placeholder="Source from" />
            <DatePickerField compact value={copySourceTo} onChange={setCopySourceTo} placeholder="Source to" min={copySourceFrom || undefined} />
          </div>
          <DatePickerField compact value={copyTargetFrom} onChange={setCopyTargetFrom} placeholder="Target from" className="w-full" />
          <button type="button" disabled={busy} onClick={runCopy} className={`w-full ${t.btnSecondary}`}>
            Copy roster block
          </button>
          <label className="block">
            <span className="text-sm text-gray-600">Import CSV</span>
            <input
              type="file"
              accept=".csv"
              className="mt-1 text-sm w-full"
              onChange={(e) => onImportCsv?.(e.target.files?.[0])}
            />
          </label>
        </div>
      </div>
    </div>
  );
}
